import { all, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { HEARINGTEMPLATE_TYPE, HEARING_TYPE } from '../../constants/api';
import {
  createHearingTypeInitialize,
  deleteHearingTypeInitialize,
  updateHearingTypeInitialize,
} from '../actions/api';
import { hearingTypeByIdSelector } from '../apiFeatures/hearingTypeSlice';
import { parseApiError } from '../globalMessageSlice';
import { awaitApiCall } from '../helpers/sagaHelpers';

// ---------- ACTIONS -----------
const setSelectedHearingTypeId = createAction(
  'page/hearingTypeAdministration/setSelectedHearingTypeId',
);
const updateHearingType = createAction(
  'page/hearingTypeAdministration/changeHearingTypeIsActive',
);
const setHearingTypeBeingUpdated = createAction(
  'page/hearingTypeAdministration/hearingTypeBeingUpdated',
);
const deleteHearingType = createAction(
  'page/hearingTypeAdministration/deleteHearingType',
);
const setIsPanelOpen = createAction(
  'page/hearingTypeAdministration/setIsPanelOpen',
);
const createHearingType = createAction(
  'page/hearingTypeAdministration/createHearingType',
);
const setIsCreatingHearingType = createAction(
  'page/hearingTypeAdministration/hearingTypeBeingCreated',
);

// ---------- REDUCERS -----------
const hearingTypeAdministrationPageReducer = createReducer(
  {
    selectedHearingTypeId: null,
    isUpdatingHearingType: false,
    isPanelOpen: false,
  },
  (builder) => {
    builder
      .addCase(setSelectedHearingTypeId, (state, action) => {
        state.selectedHearingTypeId = action.payload;
      })
      .addCase(setHearingTypeBeingUpdated, (state, action) => {
        state.isUpdatingHearingType = action.payload;
      })
      .addCase(setIsPanelOpen, (state, action) => {
        state.isPanelOpen = action.payload;
      })
      .addCase(setIsCreatingHearingType, (state, action) => {
        state.isCreatingHearingType = action.payload;
      });
  },
);
// ----------- Sagas -------------
export function* hearingTypeAdministrationSaga() {
  yield all([
    fork(changeHearingTypeIsActiveSaga),
    fork(deleteHearingTypeSaga),
    fork(createHearingTypeSaga),
  ]);
}

function* changeHearingTypeIsActiveSaga() {
  yield takeLatest(updateHearingType.type, function* (action) {
    yield put(setHearingTypeBeingUpdated(true));
    const { hearingTypeId, isActive } = action.payload;

    const hearingType = yield select(hearingTypeByIdSelector(hearingTypeId));

    const newHearingType = Object.assign({}, hearingType, {
      attributes: { ...hearingType.attributes, isActive: isActive },
      relationships: { ...hearingType.relationships },
    });

    const updateHearingTypeInitAction = yield put(
      updateHearingTypeInitialize({
        hearingTypeId,
        changes: newHearingType,
      }),
    );

    const updateHearingTypeResult = yield awaitApiCall(
      updateHearingTypeInitAction,
    );

    if (updateHearingTypeResult.error) {
      yield put(parseApiError(updateHearingTypeResult.error.payload));
    }
    yield put(setHearingTypeBeingUpdated(false));
  });
}

function* deleteHearingTypeSaga() {
  yield takeLatest(deleteHearingType.type, function* (action) {
    yield put(setHearingTypeBeingUpdated(true));

    const hearingTypeId = action.payload;

    const deleteHearingTypeInitAction = yield put(
      deleteHearingTypeInitialize(hearingTypeId),
    );

    const deleteHearingTypeResult = yield awaitApiCall(
      deleteHearingTypeInitAction,
    );

    if (deleteHearingTypeResult.succes) {
      yield put(setSelectedHearingTypeId(null));
    } else if (deleteHearingTypeResult.error) {
      yield put(parseApiError(deleteHearingTypeResult.error.payload));
    }

    yield put(setHearingTypeBeingUpdated(false));
  });
}

function* createHearingTypeSaga() {
  yield takeLatest(createHearingType.type, function* (action) {
    yield put(setIsCreatingHearingType(true));
    const { name, hearingTemplateId, isInternal } = action.payload;

    const newHearingTypeModel = {
      type: HEARING_TYPE,
      attributes: {
        name: name,
        isActive: true,
        isInternalHearing: isInternal,
      },
      relationships: {
        hearingTemplate: {
          data: {
            id: hearingTemplateId,
            type: HEARINGTEMPLATE_TYPE,
          },
        },
      },
    };

    const createHearingTypeInitAction = yield put(
      createHearingTypeInitialize({ data: newHearingTypeModel }),
    );

    const createHearingTypeResult = yield awaitApiCall(
      createHearingTypeInitAction,
    );

    if (createHearingTypeResult.error) {
      yield put(parseApiError(createHearingTypeResult.error.payload));
    }
    if (createHearingTypeResult.success) {
      yield put(setIsPanelOpen(false));
    }
    yield put(setIsCreatingHearingType(false));
  });
}

// ----------- Selectors -----------
const hearingTypeAdministrationState = (state) =>
  state.page.hearingTypeAdministration;

const selectedHearingTypeIdSelector = createSelector(
  hearingTypeAdministrationState,
  (state) => state.selectedHearingTypeId,
);
const isPanelOpenSelector = createSelector(
  hearingTypeAdministrationState,
  (state) => state.isPanelOpen,
);
const isUpdatingHearingTypeSelector = createSelector(
  hearingTypeAdministrationState,
  (state) => state.isUpdatingHearingType,
);
const isCreatingHearingTypeSelector = createSelector(
  hearingTypeAdministrationState,
  (state) => state.isCreatingHearingType,
);

export {
  setSelectedHearingTypeId,
  selectedHearingTypeIdSelector,
  updateHearingType,
  isUpdatingHearingTypeSelector,
  deleteHearingType,
  setIsPanelOpen,
  isPanelOpenSelector,
  createHearingType,
  isCreatingHearingTypeSelector,
};

export default hearingTypeAdministrationPageReducer;
